/**
 * Position and zoom an image before it is saved — "I shouldn't have to use another program"
 * (owner, Oct 2026).
 *
 * Shows the exact frame the image will be cut to: a circle for a store logo, a wide strip for a
 * banner. The original is kept alongside the cut version, so the same picture can be nudged again
 * later without hunting for the file.
 */
import { useCallback, useState } from 'react';
import Cropper from 'react-easy-crop';
import { Button } from '@/components/ui/button';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Loader2, ZoomIn, ZoomOut } from 'lucide-react';
import { toast } from 'sonner';

const PRESETS = {
  logo: { aspect: 1, cropShape: 'round', width: 400, height: 400, label: 'logo' },
  banner: { aspect: 1600 / 400, cropShape: 'rect', width: 1600, height: 400, label: 'banner' },
};

const loadImage = (src) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.addEventListener('load', () => resolve(img));
    img.addEventListener('error', reject);
    img.setAttribute('crossOrigin', 'anonymous');
    img.src = src;
  });

/** Cut the chosen area out at the size the platform actually displays. */
const cutOut = async (src, area, { width, height }) => {
  const image = await loadImage(src);
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(image, area.x, area.y, area.width, area.height, 0, 0, width, height);
  return new Promise((resolve) => canvas.toBlob(resolve, 'image/png'));
};

export default function ImageAdjustDialog({
  open, onOpenChange, src, kind = 'logo', initialCrop, saving = false, onSave,
}) {
  const preset = PRESETS[kind] || PRESETS.logo;
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(initialCrop?.zoom || 1);
  const [area, setArea] = useState(null);
  const [percent, setPercent] = useState(null);
  const [working, setWorking] = useState(false);

  const onCropComplete = useCallback((percentArea, pixels) => {
    setPercent(percentArea);
    setArea(pixels);
  }, []);

  const save = async () => {
    if (!area) return;
    setWorking(true);
    try {
      const blob = await cutOut(src, area, preset);
      // Percentages, not pixels: the stage is narrower on a phone, so a pixel frame would
      // reopen somewhere else on a different screen.
      await onSave(blob, { area_percent: percent, zoom });
    } catch {
      toast.error("We couldn't prepare your picture — try choosing it again");
    } finally {
      setWorking(false);
    }
  };

  const busy = working || saving;

  return (
    <Dialog open={open} onOpenChange={busy ? undefined : onOpenChange}>
      <DialogContent className="max-w-xl bg-slate-950 border-white/10" data-testid="image-adjust-dialog">
        <DialogHeader>
          <DialogTitle className="text-white">Position your {preset.label}</DialogTitle>
          <DialogDescription className="text-gray-400">
            Drag the picture to move it, and zoom until it sits the way you want. Everything
            outside the frame is trimmed off.
          </DialogDescription>
        </DialogHeader>

        <div className="relative w-full h-[300px] rounded-xl overflow-hidden bg-black/60"
          data-testid="image-adjust-stage">
          {src && (
            <Cropper
              image={src}
              crop={crop}
              zoom={zoom}
              aspect={preset.aspect}
              cropShape={preset.cropShape}
              showGrid={false}
              initialCroppedAreaPercentages={initialCrop?.area_percent}
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onCropComplete={onCropComplete}
            />
          )}
        </div>

        <div className="flex items-center gap-3 px-1">
          <ZoomOut className="w-4 h-4 text-gray-400 shrink-0" />
          <input
            type="range"
            min={1}
            max={4}
            step={0.01}
            value={zoom}
            onChange={(e) => setZoom(Number(e.target.value))}
            className="w-full accent-cyan-400"
            aria-label="Zoom"
            data-testid="image-adjust-zoom"
          />
          <ZoomIn className="w-4 h-4 text-gray-400 shrink-0" />
        </div>

        <DialogFooter className="gap-2 sm:gap-2">
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            disabled={busy}
            className="text-gray-300 hover:text-white hover:bg-white/10"
            data-testid="image-adjust-cancel"
          >
            Cancel
          </Button>
          <Button
            onClick={save}
            disabled={busy || !area}
            className="bg-cyan-600 hover:bg-cyan-500 text-white"
            data-testid="image-adjust-save"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : `Use this ${preset.label}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
